
import React, { useState, useCallback, useMemo } from 'react';
import type { PickupLocation, ShuttleReservation } from '../../types';
import { Icons } from '../ui/Icons';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import ConfirmationModal from '../ui/ConfirmationModal';

interface ShuttleReservationViewProps {
    pickupLocations: PickupLocation[];
    reservations: ShuttleReservation[];
    onSaveReservation: (reservation: Omit<ShuttleReservation, 'id'> & { id?: string }) => void;
    onDeleteReservation: (id: string) => void;
    selectedDate: string;
    onDateChange: (date: string) => void;
    onInitiatePrint: () => void;
}

const ShuttleReservationView: React.FC<ShuttleReservationViewProps> = ({ 
    pickupLocations, 
    reservations, 
    onSaveReservation, 
    onDeleteReservation,
    selectedDate,
    onDateChange,
    onInitiatePrint
}) => {
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [selectedDirection, setSelectedDirection] = useState<'TO_HOTEL' | 'FROM_HOTEL'>('FROM_HOTEL');
    const [selectedTime, setSelectedTime] = useState('09:00');
    const [filterTime, setFilterTime] = useState<string | null>(null);
    const [guests, setGuests] = useState(1);
    const [roomNumber, setRoomNumber] = useState('');
    const [editingReservationId, setEditingReservationId] = useState<string | null>(null);
    const [editingTime, setEditingTime] = useState('');

    const timeSlots = useMemo(() => {
        const slots = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let min = 0; min < 60; min += 30) {
                const h = hour.toString().padStart(2, '0');
                const m = min.toString().padStart(2, '0');
                slots.push(`${h}:${m}`);
            }
        }
        return slots;
    }, []);

    const deleteHandler = useDeleteHandler<ShuttleReservation>({
        onDeleteAsync: async (res) => onDeleteReservation(res.id),
        entityName: 'Reservation',
        getDisplayName: (res) => `Room ${res.roomNumber} - ${res.time}`,
        getItemId: (res) => res.id,
    });

    const getSlotData = useCallback((time: string) => {
        const slotReservations = reservations.filter(r => r.time === time);
        const totalGuests = slotReservations.reduce((sum, r) => sum + r.guests, 0);
        const locations = Array.from(new Set(slotReservations.map(r => {
            const loc = pickupLocations.find(l => l.id === r.locationId);
            return loc?.name || 'Unknown';
        })));
        return { totalGuests, locations };
    }, [reservations, pickupLocations]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedLocationId || !selectedTime || !roomNumber) return;

        onSaveReservation({
            locationId: selectedLocationId,
            direction: selectedDirection,
            time: selectedTime,
            guests,
            roomNumber,
            createdAt: new Date().toISOString()
        });

        // Reset form but keep time selected
        setGuests(1);
        setRoomNumber('');
    };

    const handleUpdateTime = (res: ShuttleReservation, newTime: string) => {
        onSaveReservation({
            ...res,
            time: newTime
        });
        setEditingReservationId(null);
    };

    return (
        <div className="max-w-7xl mx-auto space-y-4">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                        <Icons.Bus className="w-6 h-6 text-indigo-600" />
                        Shuttle Reservation System
                    </h2>
                    
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-900/50 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
                            <div className="flex items-center gap-2">
                                <Icons.Calendar className="w-3.5 h-3.5 text-indigo-600" />
                                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Reservation Date:</span>
                            </div>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => onDateChange(e.target.value)}
                                className="bg-transparent border-none text-xs font-black text-indigo-600 dark:text-indigo-400 focus:ring-0 p-0 cursor-pointer"
                            />
                        </div>
                        <button
                            onClick={onInitiatePrint}
                            className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-xl hover:bg-indigo-200 dark:hover:bg-indigo-800/50 transition-colors"
                            title="Print Daily Overview"
                        >
                            <Icons.Printer className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* 1. Route Selector */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wider">1. Select Route</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Column 1: Hotel to Locations */}
                            <div className="space-y-2">
                                <h3 className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-1 px-1">Hotel → Locations</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    {pickupLocations.map(loc => {
                                        const isSelected = selectedLocationId === loc.id && selectedDirection === 'FROM_HOTEL';
                                        return (
                                            <button
                                                key={`${loc.id}-from`}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedLocationId(loc.id);
                                                    setSelectedDirection('FROM_HOTEL');
                                                }}
                                                className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all text-left flex items-center gap-2 ${
                                                    isSelected
                                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-md scale-[1.01]'
                                                        : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-slate-600 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/20'
                                                }`}
                                            >
                                                <div className="w-2 h-2 rounded-full shadow-sm" style={{ backgroundColor: loc.color }}></div>
                                                <span className="truncate">Hotel → {loc.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Column 2: Locations to Hotel */}
                            <div className="space-y-2">
                                <h3 className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-1 px-1">Locations → Hotel</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    {pickupLocations.map(loc => {
                                        const isSelected = selectedLocationId === loc.id && selectedDirection === 'TO_HOTEL';
                                        return (
                                            <button
                                                key={`${loc.id}-to`}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedLocationId(loc.id);
                                                    setSelectedDirection('TO_HOTEL');
                                                }}
                                                className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all text-left flex items-center gap-2 ${
                                                    isSelected
                                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-md scale-[1.01]'
                                                        : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-slate-600 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/20'
                                                }`}
                                            >
                                                <div className="w-2 h-2 rounded-full shadow-sm" style={{ backgroundColor: loc.color }}></div>
                                                <span className="truncate">{loc.name} → Hotel</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. Time Slot Grid */}
                    <div className="mt-6">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wider">2. Select Time Slot (Daily Overview)</label>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5">
                            {timeSlots.map(slot => {
                                const { totalGuests, locations } = getSlotData(slot);
                                const isSelected = selectedTime === slot;
                                return (
                                    <button
                                        key={slot}
                                        type="button"
                                        onClick={() => {
                                            setSelectedTime(slot);
                                            setFilterTime(slot);
                                        }}
                                        className={`flex flex-col items-center justify-center p-1 rounded-lg border transition-all h-12 gap-0.5 ${
                                            selectedTime === slot
                                                ? 'bg-indigo-600 border-indigo-600 text-white ring-2 ring-indigo-300 ring-offset-1 dark:ring-offset-slate-800'
                                                : totalGuests > 0
                                                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50'
                                                    : 'bg-gray-50 dark:bg-slate-700/30 border-gray-100 dark:border-slate-700'
                                        } hover:scale-105`}
                                    >
                                        <div className="flex items-center gap-1 leading-none">
                                            <span className={`text-[11px] font-bold uppercase tracking-tighter ${selectedTime === slot ? 'text-white/90' : 'text-gray-400 dark:text-gray-500'}`}>{slot}</span>
                                            {totalGuests > 0 && (
                                                <span className={`text-[15px] font-black ${selectedTime === slot ? 'text-white' : 'text-indigo-600 dark:text-indigo-400'}`}>
                                                    {totalGuests}p
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="flex flex-col items-center justify-center leading-none w-full px-0.5 mt-0.5">
                                            {totalGuests > 0 ? (
                                                <div className="w-full overflow-hidden">
                                                    {locations.slice(0, 1).map((loc, i) => (
                                                        <div key={i} className={`text-[8px] font-bold px-1 py-0.5 rounded truncate w-full text-center ${selectedTime === slot ? 'bg-white/20' : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'}`}>
                                                            {loc}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className={`text-[9px] font-medium ${selectedTime === slot ? 'text-white/40' : 'text-gray-300 dark:text-gray-600'}`}>-</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* 3. Details Form */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t dark:border-slate-700">
                        <div>
                            <label htmlFor="guests" className="block text-xs font-medium text-gray-700 dark:text-gray-300">Number of Guests</label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                                    <Icons.Users className="h-3.5 w-3.5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    id="guests"
                                    min="1"
                                    max="14"
                                    value={guests}
                                    onChange={(e) => setGuests(parseInt(e.target.value))}
                                    className="block w-full pl-8 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg py-1.5 px-2.5 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="roomNumber" className="block text-xs font-medium text-gray-700 dark:text-gray-300">Room Number</label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                                    <Icons.Edit className="h-3.5 w-3.5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="roomNumber"
                                    placeholder="e.g., 402"
                                    value={roomNumber}
                                    onChange={(e) => setRoomNumber(e.target.value)}
                                    className="block w-full pl-8 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg py-1.5 px-2.5 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex items-end">
                            <button
                                type="submit"
                                disabled={!selectedLocationId || !roomNumber}
                                className="w-full h-9 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md active:scale-95"
                            >
                                Confirm Reservation at {selectedTime}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Reservation List Table */}
            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                            {filterTime ? `Reservations for ${filterTime}` : 'Active Reservations'}
                        </h2>
                        {filterTime && (
                            <button 
                                onClick={() => setFilterTime(null)}
                                className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300 rounded-md text-[10px] font-bold transition-colors flex items-center gap-1"
                            >
                                <Icons.X className="w-2.5 h-2.5" />
                                Load All
                            </button>
                        )}
                    </div>
                    <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-[10px] font-bold">
                        {reservations.length} Total
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                        <thead className="bg-gray-50 dark:bg-slate-700/50">
                            <tr>
                                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Route</th>
                                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Time</th>
                                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Guests</th>
                                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Room</th>
                                <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                            {reservations.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-xs text-gray-400 dark:text-gray-500 italic">
                                        No shuttle reservations scheduled for this date.
                                    </td>
                                </tr>
                            ) : (
                                [...reservations]
                                    .filter(res => !filterTime || res.time === filterTime)
                                    .sort((a, b) => a.time.localeCompare(b.time))
                                    .map((res) => {
                                    const location = pickupLocations.find(l => l.id === res.locationId);
                                    const routeLabel = res.direction === 'FROM_HOTEL' 
                                        ? `Hotel → ${location?.name || 'Unknown'}`
                                        : `${location?.name || 'Unknown'} → Hotel`;
                                    const isEditing = editingReservationId === res.id;
                                    
                                    return (
                                        <tr key={res.id} className={`transition-colors ${res.status === 'done' ? 'bg-green-50/50 dark:bg-green-900/10' : 'hover:bg-gray-50 dark:hover:bg-slate-700/30'}`}>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: location?.color || '#ccc' }}></div>
                                                    <span className={`text-xs font-semibold ${res.status === 'done' ? 'text-green-800 dark:text-green-200' : 'text-gray-900 dark:text-gray-100'}`}>{routeLabel}</span>
                                                </div>
                                            </td>
                                            <td className={`px-4 py-2 whitespace-nowrap text-xs font-mono font-bold ${res.status === 'done' ? 'text-green-700 dark:text-green-300' : 'text-indigo-600 dark:text-indigo-400'}`}>
                                                {isEditing ? (
                                                    <select
                                                        value={editingTime}
                                                        onChange={(e) => setEditingTime(e.target.value)}
                                                        className="bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded text-[10px] py-0.5 px-1 focus:ring-indigo-500 focus:border-indigo-500 font-bold"
                                                    >
                                                        {timeSlots.map(slot => (
                                                            <option key={slot} value={slot}>{slot}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    res.time
                                                )}
                                            </td>
                                            <td className={`px-4 py-2 whitespace-nowrap text-xs ${res.status === 'done' ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-300'}`}>{res.guests} Guests</td>
                                            <td className={`px-4 py-2 whitespace-nowrap text-xs font-medium ${res.status === 'done' ? 'text-green-800 dark:text-green-200' : 'text-gray-900 dark:text-gray-100'}`}>Room {res.roomNumber}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-right text-xs font-medium">
                                                <div className="flex items-center justify-end gap-1">
                                                    {isEditing ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleUpdateTime(res, editingTime)}
                                                                className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 p-1.5 rounded-full hover:bg-green-50 dark:hover:bg-green-900/20 transition-all"
                                                                title="Save"
                                                            >
                                                                <Icons.Save className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingReservationId(null)}
                                                                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-all"
                                                                title="Cancel"
                                                            >
                                                                <Icons.X className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => onSaveReservation({ ...res, status: res.status === 'done' ? 'pending' : 'done' })}
                                                                className={`p-1.5 rounded-full transition-all ${res.status === 'done' ? 'text-green-600 hover:text-green-800 bg-green-100 hover:bg-green-200 dark:text-green-400 dark:bg-green-900/40 dark:hover:bg-green-900/60' : 'text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'}`}
                                                                title={res.status === 'done' ? "Mark as Pending" : "Mark as Done"}
                                                            >
                                                                <Icons.Check className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingReservationId(res.id);
                                                                    setEditingTime(res.time);
                                                                }}
                                                                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 p-1.5 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
                                                                title="Edit Time"
                                                            >
                                                                <Icons.Edit className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => deleteHandler.initiateDelete(res)}
                                                                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1.5 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                                                title="Delete"
                                                            >
                                                                <Icons.Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {deleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Cancel Reservation"
                    message={`Are you sure you want to cancel the reservation for Room ${deleteHandler.itemToDelete.roomNumber} at ${deleteHandler.itemToDelete.time}?`}
                    confirmButtonText="Yes, Cancel"
                    onConfirm={deleteHandler.confirmDelete}
                    onCancel={deleteHandler.cancelDelete}
                    isConfirming={deleteHandler.isDeleting}
                />
            )}
        </div>
    );
};

export default ShuttleReservationView;
